import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import Tooltip from "@mui/material/Tooltip";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  ColumnFilter,
  GeneralActions,
  SearchSuggest,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import PaginationComponent from "Components/PaginationComponent/PaginationComponent";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Job } from "../../../../../../../../models/job";
import { JobType } from "../../../../../../../../models/jobType";
import { Filters, Searches, Sort } from "../../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../../models/statuses";
import JobTypeService from "../../../../../../../../services/job.type.service";
import OrderService from "../../../../../../../../services/order.service";
import StatusesService from "../../../../../../../../services/statuses.service";
import { Status, SubStatusUsedBy } from "../../../../../../../../util/enums";
import { saveRowsSelectedFromTable } from "../../../../../../../../util/selectRowsFromTable";
import SelectFilterOrderComponent from "../SelectFilterOrderComponent/SelectFilterOrderComponent";
import useStyles from "./styles";
import { onSearch } from "Util/onSearch";
import { useWaybillsMaintenanceContext } from "Context/WaybillsMaintenanceContext";

export type OrderIdsSelected = Array<number>;
const valueStatuses = {
  status: [
    "Transshipped",
    "In Progress",
    "Received",
    "Review",
    "Trip",
    "Late",
    "Unloaded",
    "Started",
    "Packed",
    "Lost",
    "Lead",
    "Loaded",
    "In Transit",
    "Finished",
    "Booked",
    "Assigned",
    "Advised",
  ],
};
export interface AddNewOrderHandles {
  open: (ordersIdsSelected: OrderIdsSelected) => void;
  close: () => void;
}
interface AddNewOrderProps {
  gridColumns: Column[];
  onAddOrders: (ordersSelected: Job[]) => void;
  onCancel?: () => void;
}

const AddOrderModal: ForwardRefRenderFunction<AddNewOrderHandles, AddNewOrderProps> = (
  props,
  ref
) => {
  const { gridColumns, onAddOrders } = props;

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Array<string[]>>();

  const [orders, setOrders] = useState<Job[]>([]);
  const [total, setTotal] = useState<number>(0);

  const [selecteds, setSelecteds] = useState<OrderIdsSelected>([]);
  const [modalOpened, setModalOpened] = useState(false);

  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);

  const [rows, setRows] = useState<Job[]>([]);

  const [checkedBoxType, setCheckedBoxType] = useState<Record<string, boolean>>({});
  const [checkedBoxStatus, setCheckedBoxStatus] = useState<Record<string, boolean>>({});
  const [totalCountFilter, setTotalCountFilter] = useState<number>(0);
  const { t: translate } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);
  const { currentUser } = useAppState();
  const { waybill } = useWaybillsMaintenanceContext();

  const classes = useStyles();

  const openModal = useCallback(
    (ordersIdSelected: OrderIdsSelected) => {
      setSelecteds(ordersIdSelected);
      setModalOpened(true);
      setFilters(valueStatuses);

      const defaultSearch: ColumnFilter[] = [];
      if (waybill?.originLocation?.longAddress) {
        defaultSearch.push({
          name: "origins",
          title: translate("origin"),
          value: waybill.originLocation.longAddress,
        });
      }
      if (waybill?.destinationLocation?.longAddress) {
        defaultSearch.push({
          name: "destinations",
          title: translate("destination"),
          value: waybill.destinationLocation.longAddress,
        });
      }

      if (defaultSearch.length > 0) {
        setSearchOptions(defaultSearch);
        const attributes = onSearch(defaultSearch);
        const search = attributes?.map((item) => ({
          [item.attribute]: item?.values?.[0],
        }));
        setSearches(search);
      }
    },
    [waybill, translate]
  );

  const closeModal = useCallback(() => {
    setSelecteds([]);
    setModalOpened(false);
    setPage(0);
    setPageSize(20);
    setSearches([]);
    setSearchInput("");
    setSearchOptions([]);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const { isFetching: isLoading } = useQuery(
    ["order.list", page, pageSize, sorting, filters, searches],
    () =>
      OrderService.getAll({
        page,
        pageSize,
        include: JSON.stringify([{ association: "itemsJob" }]),
        companyId: currentUser?.companyId,
        branchId: currentUser?.branches?.map((branch) => branch.id),
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        userId: currentUser?.id,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setOrders(response.data);
      },
    }
  );

  useQuery(
    ["order", filters, searches],
    () =>
      OrderService.count({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        branchId: currentUser?.branches?.map((branch) => branch.id),
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => setTotal(response?.data || 0),
    }
  );

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ORDERS,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          const statuses = ["Cancelled", "Delivered"];
          const filterList = response.data.filter(
            (status: any) => !statuses?.includes(status?.name)
          );
          setJobStatus(filterList);
        }
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Array<string[]> = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const countTrueValuesFilterBox = (data: Record<string, Record<string, boolean>>): number => {
    return Object.values(data).reduce((total, group) => {
      return total + Object.values(group).filter(Boolean).length;
    }, 0);
  };

  const transformDataFilerBox = (
    status: Record<string, boolean>,
    type: Record<string, boolean>
  ) => {
    const result: Record<string, string[]> = {};

    const typeKeys = Object.keys(type).filter((key) => type[key]);
    const statusKeys = Object.keys(status).filter((key) => status[key]);

    if (typeKeys?.length > 0) {
      result.type = typeKeys;
    }
    if (statusKeys?.length > 0) {
      result.status = statusKeys;
    }

    return result;
  };

  const renderFilterBy = () => {
    const typeOptions = jobTypes && jobTypes?.map((jobType) => jobType?.name || "");
    const statusOptions = jobStatus?.map((jobStatus) => jobStatus?.name || "");

    return (
      <SelectFilterOrderComponent
        countChecked={totalCountFilter}
        checkedStatus={checkedBoxStatus}
        checkedType={checkedBoxType}
        typeOptions={typeOptions}
        statusOptions={statusOptions}
        onFilter={(dataRetrieve: any) => {
          const count = countTrueValuesFilterBox(dataRetrieve);
          const mappingFilter = transformDataFilerBox(dataRetrieve?.status, dataRetrieve?.type);
          handleFilter(mappingFilter);
          setTotalCountFilter(count);
          setCheckedBoxType(dataRetrieve?.type);
          setCheckedBoxStatus(dataRetrieve?.status);
        }}
      />
    );
  };
  const setSelectAllRecord = () => {
    const isSelected = selecteds.length > 0;

    return (
      <Tooltip title={translate(isSelected ? "unselectAll" : "selectAll")} placement="top">
        <Button
          customColor={Color.TEXT_INFO_BLUE}
          onClick={() => {
            const updatedSelection = isSelected ? [] : orders?.map((order) => order?.id) || [];

            setSelecteds(updatedSelection);
            handleSaveOrderSelected(updatedSelection);
          }}
        >
          <span
            style={{
              color: colors.brandBlue500,
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: "16px",
              fontStyle: "normal",
            }}
          >
            {translate(isSelected ? "unselectAll" : "selectAll")}
          </span>
        </Button>
      </Tooltip>
    );
  };

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);
    const search = attributes?.map((item) => ({
      [item.attribute]: item?.values?.[0],
    }));
    handleSearch(search);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: "reference", title: `${translate("reference")}` },
          { name: "customer", title: translate("customer") },
          { name: "origins", title: translate("origin") },
          { name: "destinations", title: translate("destination") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={(value) => {
          setSearchInput(value);
        }}
        placeholder="Search"
      />
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["suggestSearch", "selectAll"],
      right: ["renderFilterBy"],
    },
    renderFilterBy: renderFilterBy(),
    selectAll: setSelectAllRecord(),
    suggestSearch: renderSearchSuggest(),
  };

  const handleCurrentPage = (numberOfPage: number) => {
    setSelecteds([]);
    setPage(numberOfPage - 1);
  };

  const onCallAddOrders = () => {
    onAddOrders(rows);
    closeModal();
  };

  const handleSaveOrderSelected = (rowsArr: number[]) => {
    const rowsSelected: Job[] | any = saveRowsSelectedFromTable(rowsArr, rows, orders);
    setRows([...rowsSelected]);
  };

  if (!modalOpened) return null;

  return (
    <Modal
      open={modalOpened}
      handleClose={closeModal}
      title={translate("selectOrders")}
      showActionButtons
      className={classes.modal}
      actionButtonProps={{
        onSave: onCallAddOrders,
        onCancel: closeModal,
        dataCySave: "button-add-orders",
        textSave: `${translate("addSelectedOption")} ${
          selecteds?.length ? `(${selecteds.length})` : ""
        }`,
        saveDisabled: !selecteds.length,
      }}
    >
      <div
        data-cy="waybills-add-orders-data-list"
        className={classes.container}
        key={JSON.stringify(selecteds)}
      >
        <ShipeeziNewGrid
          data={orders}
          columns={gridColumns}
          keyExtractor={(data) => data.id}
          totalItems={total}
          loadingData={isLoading}
          emptyData={
            isLoading ? `${translate("loadingOrders")}...` : translate("noInformationToBeShown")
          }
          selecteds={selecteds}
          handleSelect={(rows: number[]) => {
            setSelecteds(rows);
            handleSaveOrderSelected(rows);
          }}
          positionActionsColumn="right"
          onSorting={handleSort}
          generalActions={generalActions}
          pageSize={pageSize}
          currentPage={page}
          pageSizeList={[20, 40, 60, 100]}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          onChangePageSize={() => {
            setPageSize(pageSize);
            handleCurrentPage(1);
            setSelecteds([]);
          }}
        />
      </div>
      {/* <PaginationComponent
        pageSizeOptions={[20, 40, 60, 100]}
        changePageSize={(pageSize: number) => {
          setPageSize(pageSize);
          handleCurrentPage(1);
          setSelecteds([]);
        }}
        currentPage={page}
        pageSize={pageSize}
        total={total}
        changePage={(page: number) => {
          handleCurrentPage(page);
        }}
      /> */}
    </Modal>
  );
};

export default forwardRef(AddOrderModal);
